import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { SafeImage } from "@/components/ui/SafeImage";
import type { PortfolioItem } from "@/features/portfolio-items/types";
import { getPortfolioImage, getPortfolioTags, getShortDescription } from "./portfolio-utils";

interface PortfolioCardProps {
  item: PortfolioItem;
}

export function PortfolioCard({ item }: PortfolioCardProps) {
  const image = getPortfolioImage(item);
  const tags = getPortfolioTags(item);

  return (
    <motion.article
      whileHover={{ y: -6 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="group min-w-0 overflow-hidden rounded-lg border border-cyan-300/20 bg-black/60 shadow-[0_0_35px_rgba(34,211,238,0.08)] transition hover:border-cyan-300/70 hover:shadow-neon"
    >
      <Link to={`/portfolio/${item.slug}`} className="block h-full">
        <div className="aspect-[16/10] overflow-hidden bg-cyan-300/5">
          {image ? (
            <SafeImage
              src={image.url}
              alt={image.alt || item.title}
              fallbackLabel="No image"
              className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <div className="flex h-full items-center justify-center border-b border-cyan-300/10 bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.16),transparent_62%)] text-sm uppercase tracking-[0.24em] text-cyan-100/70">
              No Image
            </div>
          )}
        </div>

        <div className="p-5">
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span key={tag} className="rounded border border-teal-300/30 bg-teal-300/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-teal-100">
                {tag}
              </span>
            ))}
          </div>
          <h2 className="mt-4 break-words font-display text-xl font-bold uppercase text-white sm:text-2xl">{item.title}</h2>
          <p className="mt-2 text-sm leading-6 text-content-secondary">{getShortDescription(item.description)}</p>
          <div className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-cyan-200">View Project</div>
        </div>
      </Link>
    </motion.article>
  );
}
